import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";
import MDButton from "components/MDButton";
import { LojaPageContainer } from "../../common";
import { resolverCentroCustoIdEvento } from "../dashboard/helpers/resolver-centro-custo-evento";
import { useCentrosCusto } from "../dashboard/hooks/useCentrosCusto";
import { useContasPagar } from "../dashboard/hooks/useContasPagar";
import { useFornecedoresFinanceiro } from "../dashboard/hooks/useFornecedoresFinanceiro";
import { useContasFinanceirasFinanceiro } from "../dashboard/hooks/useContasFinanceirasFinanceiro";
import { useBaixaTitulo } from "../dashboard/hooks/useBaixaTitulo";
import { useCancelamentoTitulo } from "../dashboard/hooks/useCancelamentoTitulo";
import { useCriarTituloPagar } from "../dashboard/hooks/useCriarTituloPagar";
import { useEditarTituloPagar } from "../dashboard/hooks/useEditarTituloPagar";
import ContasPagarSection from "../dashboard/components/ContasPagarSection";
import DespesasFixasModal from "../dashboard/components/DespesasFixasModal";
import BaixaTituloDialog from "../dashboard/components/BaixaTituloDialog";
import CancelamentoTituloDialog from "../dashboard/components/CancelamentoTituloDialog";
import NovaContaPagarDialog from "../dashboard/components/NovaContaPagarDialog";

/**
 * Contas a pagar operacionais — GET /loja/financeiro/titulos-pagar
 */
export default function LojaFinanceiroContasPagarPage() {
  const [despesasFixasOpen, setDespesasFixasOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const eventoAbertoRef = useRef<string | null>(null);
  const centros = useCentrosCusto({ ativo: true, somenteEventosAtivos: true });
  const contasPagar = useContasPagar();
  const fornecedoresFinanceiro = useFornecedoresFinanceiro();
  const contasFinanceirasFinanceiro = useContasFinanceirasFinanceiro();

  const handleMutacaoSucesso = useCallback(() => {
    contasPagar.refetch();
    fornecedoresFinanceiro.refetch();
    contasFinanceirasFinanceiro.refetch();
  }, [contasPagar, fornecedoresFinanceiro, contasFinanceirasFinanceiro]);

  const criarTitulo = useCriarTituloPagar({
    onSuccess: handleMutacaoSucesso,
  });

  const editarTitulo = useEditarTituloPagar({
    onSuccess: handleMutacaoSucesso,
  });

  const baixaTitulo = useBaixaTitulo({
    onSuccess: handleMutacaoSucesso,
    contasFinanceiras: contasFinanceirasFinanceiro.data,
  });

  const cancelamentoTitulo = useCancelamentoTitulo({
    onSuccess: handleMutacaoSucesso,
  });

  const eventoIdParam = searchParams.get("novaDespesaEvento");
  useEffect(() => {
    if (!eventoIdParam || eventoAbertoRef.current === eventoIdParam) return;
    const id = Number(eventoIdParam);
    if (!Number.isFinite(id) || id <= 0) return;

    let cancelled = false;
    const run = async () => {
      eventoAbertoRef.current = eventoIdParam;
      const nome =
        (location.state as { eventoNome?: string } | null)?.eventoNome ?? "";
      const centroCustoId = await resolverCentroCustoIdEvento(id, centros.data);
      if (cancelled) return;
      if (centroCustoId) {
        void centros.refetch();
      }
      criarTitulo.abrir({
        contexto: "EVENTO",
        eventoId: id,
        categoria: "SERVICOS",
        descricao: nome ? `Despesa — ${nome}` : "",
        centroCustoId,
      });
      const next = new URLSearchParams(searchParams);
      next.delete("novaDespesaEvento");
      setSearchParams(next, { replace: true });
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [
    eventoIdParam,
    location.state,
    searchParams,
    setSearchParams,
    criarTitulo,
    centros,
  ]);

  return (
    <LojaPageContainer
      title="Contas a pagar"
      subtitle="Títulos a pagar, lançamento manual, baixa e cancelamento."
      actions={
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <MDButton
            variant="outlined"
            color="dark"
            size="small"
            onClick={() => setDespesasFixasOpen(true)}
          >
            Despesas fixas
          </MDButton>
          <MDButton
            variant="gradient"
            color="info"
            size="small"
            onClick={() => criarTitulo.abrir()}
          >
            Nova conta a pagar
          </MDButton>
        </Stack>
      }
    >
      <Stack spacing={3}>
        {criarTitulo.feedback ? (
          <Alert
            severity={criarTitulo.feedback.severity}
            onClose={criarTitulo.clearFeedback}
            sx={{ borderRadius: 2 }}
          >
            {criarTitulo.feedback.message}
          </Alert>
        ) : null}
        {baixaTitulo.feedback ? (
          <Alert
            severity={baixaTitulo.feedback.severity}
            onClose={baixaTitulo.clearFeedback}
            sx={{ borderRadius: 2 }}
          >
            {baixaTitulo.feedback.message}
          </Alert>
        ) : null}
        {cancelamentoTitulo.feedback ? (
          <Alert
            severity={cancelamentoTitulo.feedback.severity}
            onClose={cancelamentoTitulo.clearFeedback}
            sx={{ borderRadius: 2 }}
          >
            {cancelamentoTitulo.feedback.message}
          </Alert>
        ) : null}
        {editarTitulo.feedback ? (
          <Alert
            severity={editarTitulo.feedback.severity}
            onClose={editarTitulo.clearFeedback}
            sx={{ borderRadius: 2 }}
          >
            {editarTitulo.feedback.message}
          </Alert>
        ) : null}

        <ContasPagarSection
          contasPagar={contasPagar}
          centros={centros}
          fornecedores={fornecedoresFinanceiro}
          tabelaVariant="operacional"
          onBaixarTitulo={
            baixaTitulo.podeBaixaManual
              ? (titulo) =>
                  baixaTitulo.abrirBaixaPagar(
                    titulo,
                    contasFinanceirasFinanceiro.data
                  )
              : undefined
          }
          onCancelarTitulo={
            cancelamentoTitulo.podeCancelarTitulo
              ? cancelamentoTitulo.abrirCancelamentoPagar
              : undefined
          }
          onEditarTitulo={
            editarTitulo.podeEditar ? editarTitulo.abrir : undefined
          }
        />

        <DespesasFixasModal
          open={despesasFixasOpen}
          onClose={() => setDespesasFixasOpen(false)}
          fornecedores={fornecedoresFinanceiro.data}
          onRefetchFornecedores={fornecedoresFinanceiro.refetch}
          onTitulosGerados={handleMutacaoSucesso}
        />

        <NovaContaPagarDialog
          open={criarTitulo.open}
          form={criarTitulo.form}
          setForm={criarTitulo.setForm}
          loading={criarTitulo.loading}
          error={criarTitulo.error}
          centros={centros.data}
          fornecedores={fornecedoresFinanceiro.data}
          onRefetchFornecedores={fornecedoresFinanceiro.refetch}
          onClose={criarTitulo.fechar}
          onConfirm={() => void criarTitulo.confirmar()}
          variant="create"
        />

        <NovaContaPagarDialog
          open={editarTitulo.open}
          form={editarTitulo.form}
          setForm={editarTitulo.setForm}
          loading={editarTitulo.loading}
          error={editarTitulo.error}
          centros={centros.data}
          fornecedores={fornecedoresFinanceiro.data}
          onRefetchFornecedores={fornecedoresFinanceiro.refetch}
          onClose={editarTitulo.fechar}
          onConfirm={() => void editarTitulo.confirmar()}
          variant="edit"
        />

        <BaixaTituloDialog
          dialog={baixaTitulo.dialog}
          form={baixaTitulo.form}
          setForm={baixaTitulo.setForm}
          contasFinanceiras={contasFinanceirasFinanceiro.data}
          contasLoading={contasFinanceirasFinanceiro.loading}
          contasError={contasFinanceirasFinanceiro.error}
          loading={baixaTitulo.loading}
          error={baixaTitulo.error}
          onClose={baixaTitulo.fecharDialog}
          onConfirm={baixaTitulo.confirmarBaixa}
        />

        <CancelamentoTituloDialog
          dialog={cancelamentoTitulo.dialog}
          form={cancelamentoTitulo.form}
          setForm={cancelamentoTitulo.setForm}
          loading={cancelamentoTitulo.loading}
          error={cancelamentoTitulo.error}
          onClose={cancelamentoTitulo.fecharDialog}
          onConfirm={cancelamentoTitulo.confirmarCancelamento}
        />
      </Stack>
    </LojaPageContainer>
  );
}
